import { useMemo } from "react";
import { Filter, Info, X } from "lucide-react";

import { Input, Select } from "@/components/atoms";
import { ChevronDownIcon } from "@/components/icons";
import {
    DraftDataFilter,
    DataFilterFieldOption,
    DataFilterOperator,
    DataFilterScope,
    OPERATOR_LABELS,
    isDraftFilterComplete,
    operatorsForKind,
} from "@/modules/widgets/types/dataFilters";
import { WIDGET_TYPES_WITHOUT_FILTERS, WIDGET_TYPE_LABELS, WidgetTypeOption } from "./types";

interface DataFiltersSectionProps {
    filters: DraftDataFilter[];
    fieldOptions: DataFilterFieldOption[];
    onChange: (filters: DraftDataFilter[]) => void;
    isOpen: boolean;
    onToggleOpen: () => void;
    emptyFieldsMessage?: string;
    widgetType?: WidgetTypeOption | null;
}

function makeEmptyFilter(): DraftDataFilter {
    return { field: "", operator: "", value: "", enabled: true, connector: "AND" };
}

export function DataFiltersSection({ filters, fieldOptions, onChange, isOpen, onToggleOpen, emptyFieldsMessage, widgetType }: DataFiltersSectionProps) {
    const optionByField = useMemo(() => {
        const map = new Map<string, DataFilterFieldOption>();
        fieldOptions.forEach((opt) => map.set(opt.field, opt));
        return map;
    }, [fieldOptions]);

    const activeCount = filters.filter((f) => f.enabled !== false && isDraftFilterComplete(f)).length;
    const hasNoFieldOptions = fieldOptions.length === 0;
    const isUnsupportedWidgetType = !!widgetType && (WIDGET_TYPES_WITHOUT_FILTERS as readonly string[]).includes(widgetType);
    // Scope toggle is only meaningful when the widget has a segment configured
    // (so there's a "segment-aggregated row" distinct from the expansion rows).
    const hasSegment = useMemo(() => fieldOptions.some((opt) => opt.field === "segmentValue"), [fieldOptions]);

    const handleAddFilter = () => {
        onChange([...filters, makeEmptyFilter()]);
    };

    const handleRemoveFilter = (index: number) => {
        onChange(filters.filter((_, i) => i !== index));
    };

    const handleFieldChange = (index: number, fieldKey: string) => {
        const current = filters[index];
        const prevKind = optionByField.get(current.field)?.kind;
        const nextKind = optionByField.get(fieldKey)?.kind;
        const kindChanged = prevKind !== nextKind;

        const next = filters.map((f, i) =>
            i === index
                ? {
                      ...f,
                      field: fieldKey,
                      // AC 16 — reset operator + value on field kind change
                      operator: kindChanged ? "" : f.operator,
                      value: kindChanged ? "" : f.value,
                  }
                : f,
        );
        onChange(next);
    };

    const handleOperatorChange = (index: number, op: DataFilterOperator) => {
        onChange(filters.map((f, i) => (i === index ? { ...f, operator: op } : f)));
    };

    const handleValueChange = (index: number, raw: string, kind: "string" | "number") => {
        const value: string | number = kind === "number" ? (raw === "" ? "" : Number(raw)) : raw;
        onChange(filters.map((f, i) => (i === index ? { ...f, value } : f)));
    };

    const handleToggleEnabled = (index: number) => {
        onChange(filters.map((f, i) => (i === index ? { ...f, enabled: f.enabled === false ? true : false } : f)));
    };

    const handleScopeChange = (index: number, scope: DataFilterScope) => {
        onChange(filters.map((f, i) => (i === index ? { ...f, scope } : f)));
    };

    return (
        <div className="p-4 rounded border border-muted">
            <button type="button" className="flex items-center gap-2 w-full" onClick={onToggleOpen}>
                <Filter className="w-5 h-5" />
                <span className="font-roboto">Filters</span>
                {!isOpen && !isUnsupportedWidgetType && activeCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full bg-[#eef0f1] text-[#5a717a] text-xs px-1.5 font-roboto">
                        {activeCount}
                    </span>
                )}
                <ChevronDownIcon className={`w-5 h-5 ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {isOpen && (
                <div className="mt-4">
                    {isUnsupportedWidgetType ? (
                        <div className="flex items-start gap-2 rounded-md border border-[#cfe9e8] bg-[#e8f5f4] px-3 py-2.5 text-[12px] text-[#0a6b67] font-roboto">
                            <Info className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>
                                Filters aren't supported for {WIDGET_TYPE_LABELS[widgetType as WidgetTypeOption]} widgets. Choose a different widget type to add filters.
                            </span>
                        </div>
                    ) : hasNoFieldOptions ? (
                        <p className="text-[12px] text-[#5a717a] font-roboto">
                            {emptyFieldsMessage ?? "Select a level, segment, or metric above to enable filters."}
                        </p>
                    ) : (
                        <>
                            <div className="space-y-3">
                                {filters.map((filter, index) => {
                                    const option = optionByField.get(filter.field);
                                    const kind = option?.kind ?? "string";
                                    const operators = operatorsForKind(kind);
                                    const isDisabled = filter.enabled === false;
                                    const operatorDisabled = !filter.field;

                                    return (
                                        <div key={index} className="relative">
                                            {index > 0 && (
                                                <div className="flex items-center gap-2 -mt-1 mb-1.5">
                                                    <span className="inline-block rounded bg-[#eef0f1] text-[#5a717a] text-[10px] font-roboto font-medium tracking-wider px-2 py-0.5">
                                                        AND
                                                    </span>
                                                    <div className="flex-1 border-t border-dashed border-[#e3eaed]" />
                                                </div>
                                            )}
                                            <div className={`rounded-md border border-[#e3eaed] bg-white p-2.5 space-y-2 transition-colors ${isDisabled ? "opacity-60" : "hover:border-[#cbd6da]"}`}>
                                                <Select
                                                    options={fieldOptions.map((opt) => ({ id: opt.field, label: opt.label }))}
                                                    value={filter.field || null}
                                                    onChange={(val) => handleFieldChange(index, String(val ?? ""))}
                                                    placeholder="Field"
                                                    clearable={false}
                                                    searchable
                                                />
                                                <div className="flex items-stretch gap-2">
                                                    <div className="basis-[7.5rem] shrink-0">
                                                        <Select
                                                            options={operators.map((op) => ({ id: op, label: OPERATOR_LABELS[op] }))}
                                                            value={filter.operator || null}
                                                            onChange={(val) => handleOperatorChange(index, String(val) as DataFilterOperator)}
                                                            placeholder="Operator"
                                                            clearable={false}
                                                            disabled={operatorDisabled}
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <Input
                                                            type={kind === "number" ? "number" : "text"}
                                                            value={filter.value === undefined || filter.value === null ? "" : String(filter.value)}
                                                            onChange={(e) => handleValueChange(index, e.target.value, kind)}
                                                            placeholder="Value"
                                                            disabled={isDisabled || !filter.field}
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-1 shrink-0 pl-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleEnabled(index)}
                                                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                                                isDisabled ? "bg-[#d9e0e3]" : "bg-[#079a95]"
                                                            }`}
                                                            aria-label={isDisabled ? "Enable filter" : "Disable filter"}
                                                            title={isDisabled ? "Filter disabled — click to enable" : "Filter enabled — click to disable"}
                                                        >
                                                            <span
                                                                className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                                                    isDisabled ? "translate-x-0.5" : "translate-x-[1.125rem]"
                                                                }`}
                                                            />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveFilter(index)}
                                                            className="rounded p-1 text-[#97a7ad] hover:bg-[#f3f5f6] hover:text-[#d14343] transition-colors"
                                                            aria-label="Remove filter"
                                                            title="Remove filter"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                                {/* Scope only matters when filtering a metric on a segmented widget — a metric value differs between the aggregated segment row and its per-row children. */}
                                                {hasSegment && kind === "number" && filter.field !== "segmentValue" && (
                                                    <div className="flex items-center gap-2 pt-0.5">
                                                        <span className="text-[11px] text-[#5a717a] font-roboto whitespace-nowrap">Apply to</span>
                                                        <div className="inline-flex rounded-md border border-[#d9e0e3] p-0.5">
                                                            {(["segment", "all"] as const).map((scopeValue) => {
                                                                const current = (filter.scope ?? "all") === scopeValue;
                                                                return (
                                                                    <button
                                                                        key={scopeValue}
                                                                        type="button"
                                                                        onClick={() => handleScopeChange(index, scopeValue)}
                                                                        disabled={isDisabled}
                                                                        className={`rounded px-2 py-0.5 text-[11px] font-roboto transition-colors ${
                                                                            current ? "bg-[#079a95] text-white" : "text-[#5a717a] hover:bg-[#f3f5f6]"
                                                                        }`}
                                                                    >
                                                                        {scopeValue === "all" ? "Nested rows" : "Top level"}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <button
                                type="button"
                                onClick={handleAddFilter}
                                className="mt-3 inline-flex items-center gap-1 text-[#079a95] hover:text-[#067a76] font-roboto text-sm font-medium"
                            >
                                <span className="text-base leading-none">+</span> Add filter
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
